import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, DatePicker, Descriptions, Input, InputNumber, message, Modal, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline, type ApprovalLine } from "../../components/ApprovalTimeline";
import { ActivityScopeSelect } from "../../components/ActivityScopeSelect";
import { QtyInput } from "../../components/QtyInput";
import { DocumentPrintSheet } from "../../components/DocumentPrintSheet";
import { SiteLabel } from "../../components/SiteLabel";
import { SiteSelect } from "../../components/SiteSelect";
import { bomItemActivityPath, bomItemBalance, bomItemsInScope, flattenActivities } from "../../helpers/activities";
import { useApi } from "../../helpers/useApi";
import { hasPermission, hasProjectGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useSitesQuery } from "../../helpers/queries";
import { DT_FORMATS } from "../../helpers/constants";
import { fmtItem, fmtSite } from "../../helpers/format";
import type {
  MRQ,
  MRQLine,
  BOMItem,
  BOMActivity,
  ProjectBOMVersion,
  Paginated,
  AllocationProposal,
  PurchaseRequisition,
  MRQRelatedDoc,
  MRQRelatedDocs,
  FulfillmentAllocation,
} from "../../api-models";

interface DraftLine {
  key: string;
  bomItemId?: number;
  requestedQty?: number;
}

const NO_RELATED_DOCS: MRQRelatedDocs = { requisitions: [], issues: [], ginDirects: [], transfers: [], receipts: [] };

// One row of chips per downstream document type. A GIN or MRN carries no status of its
// own (it is a posting, not an approvable), so its chip falls back to the date it was
// posted rather than showing an empty tag.
function RelatedDocRow({ label, docs, to }: { label: string; docs: MRQRelatedDoc[]; to: (d: MRQRelatedDoc) => string }) {
  const navigate = useNavigate();
  if (docs.length === 0) return null;
  // Space is inline-flex, so each row needs its own block wrapper — without it the groups
  // run together into one flowing line of chips.
  return (
    <div style={{ marginBottom: 8 }}>
      <Space wrap>
        <span>{label}:</span>
        {docs.map((d) => (
          <Button key={d.id} size="small" onClick={() => navigate(to(d))}>
            {d.docNo}{" "}
            {d.status ? <DocumentStatusTag status={d.status} /> : d.date ? <Tag>{dayjs(d.date).format(DT_FORMATS.date)}</Tag> : null}
          </Button>
        ))}
      </Space>
    </div>
  );
}

// Outstanding qty per allocation source across the whole request — this is what decides
// which downstream document is actually available (spec §10.2). CW feeds an issue and
// SITE_TRANSFER a transfer order, but the two PROCUREMENT_* buckets are not dispatchable
// at all: they have to be bought first, and only an accepted GRN converts them into a CW
// allocation this request can issue against (spec §14.1/§31.19).
function outstandingBySource(mrq: MRQ | null): Record<string, number> {
  const out: Record<string, number> = {};
  for (const l of mrq?.lines ?? []) {
    for (const a of l.allocations ?? []) {
      if (a.status === "CANCELLED") continue;
      out[a.sourceType] = (out[a.sourceType] ?? 0) + (a.qty - a.cancelledQty - a.dispatchedQty);
    }
  }
  return out;
}

export default function RequestDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: sites = [] } = useSitesQuery();

  const isNew = id === "new";
  // Raised from a project page: the project is already known, so only its sites are offered.
  const fromProjectId = Number(searchParams.get("projectId")) || undefined;
  const [mrq, setMrq] = useState<MRQ | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [siteId, setSiteId] = useState<number>();
  const [neededBy, setNeededBy] = useState<dayjs.Dayjs>();
  const [bomItems, setBomItems] = useState<BOMItem[]>([]);
  const [activityTree, setActivityTree] = useState<BOMActivity[]>([]);
  // The work this request is for. Picked before any material is: the picker below scopes
  // what the line editor offers, and the server refuses a line outside it (mrq.go).
  const [activityIds, setActivityIds] = useState<number[]>([]);
  const [docs, setDocs] = useState<MRQRelatedDocs>(NO_RELATED_DOCS);

  const activities = useMemo(() => flattenActivities(activityTree), [activityTree]);

  const site = sites.find((s) => s.id === (isNew ? siteId : mrq?.siteId));
  const selectableSites = fromProjectId ? sites.filter((s) => s.projectId === fromProjectId) : sites;

  async function loadBomItemsForProject(projectId: number) {
    const versions = await request<Paginated<ProjectBOMVersion>>(
      { url: "/bom-versions", params: { projectId, status: "APPROVED_ACTIVE", pageSize: 1 } },
      { silent: true },
    );
    if (versions.data.length === 0) {
      setBomItems([]);
      setActivityTree([]);
      return;
    }
    applyVersion(await request<ProjectBOMVersion>({ url: `/bom-versions/${versions.data[0].id}` }, { silent: true }));
  }

  // Only MAPPED items can carry a balance (BOMItem.Requestable in bom.go), so they are the
  // only ones worth offering; the activity tree comes along because the request is scoped
  // to it before any material is chosen.
  function applyVersion(detail: ProjectBOMVersion) {
    setBomItems((detail.items ?? []).filter((l) => l.mapping === "MAPPED"));
    setActivityTree(detail.activities ?? []);
  }

  async function load() {
    if (isNew) return;
    const res = await request<MRQ>({ url: `/mrqs/${id}` });
    setMrq(res);
    setLines((res.lines ?? []).map((l) => ({ key: String(l.id), bomItemId: l.bomItemId, requestedQty: l.requestedQty })));
    setActivityIds((res.activities ?? []).map((a) => a.activityId));
    if (res.bomVersionId) {
      // The request stays pinned to the version that authorized it (spec §6.3), so its
      // items and activity names come from that version, not whatever is active now.
      applyVersion(await request<ProjectBOMVersion>({ url: `/bom-versions/${res.bomVersionId}` }, { silent: true }));
    }
    // The whole fulfillment chain raised off this request — requisitions, issues, transfer
    // orders and receipts — so every downstream document is traceable from here and a buy
    // or dispatch is not blindly raised twice.
    const related = await request<MRQRelatedDocs>({ url: `/mrqs/${res.id}/documents` }, { silent: true }).catch(() => null);
    setDocs(related ?? NO_RELATED_DOCS);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // A lone selectable site is picked by SiteSelect itself, which is what makes the BOM
  // items below load without the user touching the picker.
  useEffect(() => {
    if (isNew && siteId) {
      const s = sites.find((x) => x.id === siteId);
      if (s) loadBomItemsForProject(s.projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, siteId, sites.length]);

  const canWrite = hasPermission(user, "Requests.Write", mrq?.siteId ?? siteId);
  const canAllocate = hasPermission(user, "Requests.Allocate", mrq?.siteId ?? siteId);
  const editable = isNew || mrq?.status === "DRAFT";
  const showAllocationPanel = !isNew && !!mrq && mrq.status !== "DRAFT" && mrq.status !== "PENDING_APPROVAL" && mrq.status !== "REJECTED" && mrq.status !== "CANCELLED";
  const canDraftPR = !!mrq && hasProjectGrant(user, "PR.Write", mrq.projectId);

  const outstanding = outstandingBySource(mrq);
  const cwOutstanding = outstanding["CW"] ?? 0;
  const transferOutstanding = outstanding["SITE_TRANSFER"] ?? 0;
  const procurementOutstanding = (outstanding["PROCUREMENT_CW"] ?? 0) + (outstanding["PROCUREMENT_DIRECT"] ?? 0);
  const livePRs = docs.requisitions.filter((p) => p.status !== "CANCELLED" && p.status !== "REJECTED");

  const bomItemById = new Map(bomItems.map((l) => [l.id, l]));

  // What the approver ticks off. Everything is offered checked; a line already declined at
  // an earlier step is left out entirely - it cannot be declined twice, and re-offering it
  // would suggest the approver could put it back.
  const approvalLines: ApprovalLine[] = (mrq?.lines ?? [])
    .filter((l) => !l.rejected)
    .map((l) => {
      const bl = bomItemById.get(l.bomItemId);
      return {
        id: l.id,
        label: `${l.item ? fmtItem(l.item) : l.itemId} - ${l.requestedQty} ${l.item?.unit?.abbreviation ?? ""}`.trim(),
        detail: bl ? bomItemActivityPath(bl, activities) : undefined,
      };
    });

  // Only the materials under the selected work are on offer. Picking a top-level activity
  // takes its whole branch with it, which is the same expansion the server applies — so
  // what the picker shows is exactly what a save will accept.
  const scopedBomItems = useMemo(() => bomItemsInScope(bomItems, activityIds, activities), [bomItems, activityIds, activities]);

  // Materials are picked under the piece of work they belong to, so the options are
  // grouped by activity path and the groups keep the tree's own display order.
  const groupedBomItemOptions = (() => {
    const groups = new Map<string, { value: number; label: string }[]>();
    for (const l of scopedBomItems) {
      const path = bomItemActivityPath(l, activities);
      const opts = groups.get(path) ?? [];
      opts.push({ value: l.id, label: `${l.item ? fmtItem(l.item) : l.itemId} — balance ${bomItemBalance(l)}` });
      groups.set(path, opts);
    }
    return [...groups.entries()].map(([label, options]) => ({ label, options }));
  })();

  function addLine() {
    setLines((prev) => [...prev, { key: `new-${Date.now()}-${Math.random()}` }]);
  }
  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }
  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }

  // Narrowing the work narrows the request: a line whose material is no longer in scope
  // would be refused at save, so it is dropped here with a note rather than left to fail.
  function changeActivities(ids: number[]) {
    setActivityIds(ids);
    const stillInScope = new Set(bomItemsInScope(bomItems, ids, activities).map((l) => l.id));
    setLines((prev) => {
      const kept = prev.filter((l) => l.bomItemId == null || stillInScope.has(l.bomItemId));
      if (kept.length < prev.length) {
        message.info(`${prev.length - kept.length} line(s) removed — their activity is no longer selected.`);
      }
      return kept;
    });
  }

  // The BOM balance is the server's hard ceiling on a requested qty (validateMRQItems,
  // spec §7), so it caps the input here too — a request that only fails at save is a
  // worse way to learn the line is over-drawn. One row per remaining BOM item in the
  // selected work, each pre-filled with its whole balance, is the common "request
  // everything this activity still needs" case.
  function addAllFromScope() {
    const used = new Set(lines.map((l) => l.bomItemId));
    const stamp = Date.now();
    const additions = scopedBomItems
      .filter((l) => !used.has(l.id) && bomItemBalance(l) > 0.0005)
      .map((l, i) => ({ key: `new-${stamp}-${i}`, bomItemId: l.id, requestedQty: bomItemBalance(l) }));
    if (additions.length === 0) {
      message.info("Every item in the selected activities with a remaining balance is already on this request.");
      return;
    }
    setLines((prev) => [...prev, ...additions]);
  }

  async function save() {
    if (activityIds.length === 0) {
      message.error("Select the activities this request is for.");
      return;
    }
    const payload = {
      siteId: isNew ? siteId : mrq!.siteId,
      neededBy: neededBy?.toISOString(),
      notes: "",
      activityIds,
      items: lines
        .filter((l) => l.bomItemId && l.requestedQty)
        // InputNumber only clamps to max on blur, so a qty typed and saved in one motion
        // can still be over the balance — clamp again rather than send a doomed payload.
        .map((l) => {
          const bl = bomItemById.get(l.bomItemId!);
          return { bomItemId: l.bomItemId, requestedQty: bl ? Math.min(l.requestedQty!, bomItemBalance(bl)) : l.requestedQty };
        }),
    };
    if (isNew) {
      const created = await request<MRQ>({ method: "POST", url: "/mrqs", data: payload });
      message.success("Request created.");
      navigate(`/requests/${created.id}`, { replace: true });
    } else {
      await request({ method: "PUT", url: `/mrqs/${id}`, data: payload });
      message.success("Request updated.");
      load();
    }
  }

  async function submit() {
    await request({ method: "POST", url: `/mrqs/${id}/submit` });
    message.success("Submitted for approval.");
    load();
  }

  async function cancelMRQ() {
    await request({ method: "POST", url: `/mrqs/${id}/cancel` });
    message.success("Request cancelled.");
    load();
  }

  async function applyProposal(lineId: number) {
    const proposal = await request<AllocationProposal[]>({ url: `/mrq-lines/${lineId}/allocation-proposal` }, { silent: true });
    const deltas = proposal
      .filter((p) => !p.advisory)
      .map((p) => ({ sourceType: p.sourceType, sourceSiteId: p.sourceSiteId, qtyDelta: p.qty }));
    if (deltas.length === 0) {
      message.info("Nothing to allocate — no CW stock or remaining quantity on this line.");
      return;
    }
    await request({ method: "POST", url: `/mrq-lines/${lineId}/allocations`, data: { deltas } });
    message.success("Allocation applied.");
    load();
  }

  // Give up on one source without giving up on the material: a negative delta shrinks the
  // allocation, returning the quantity to unallocated so it can be sourced again. Used when
  // a purchase will not deliver — the request still needs the goods.
  async function releaseAllocation(lineId: number, allocationId: number, qty: number, reason: string) {
    await request({
      method: "POST",
      url: `/mrq-lines/${lineId}/allocations`,
      data: { deltas: [{ allocationId, qtyDelta: -qty, reason }] },
    });
    message.success("Allocation released — the quantity is available to allocate again.");
    load();
  }

  // Procurement allocations are only a decision to buy — this is the step that turns them
  // into an actual requisition, and the only route by which a PROCUREMENT_CW qty ever
  // becomes issuable stock for this request.
  async function draftPR() {
    const created = await request<PurchaseRequisition>({ method: "POST", url: `/prs/from-request/${id}` });
    message.success(`Requisition ${created.docNo} drafted.`);
    navigate(`/procurement/prs/${created.id}`);
  }

  async function cancelLineQty(lineId: number, qty: number, reason: string) {
    await request({ method: "POST", url: `/mrq-lines/${lineId}/cancel`, data: { qty, reason } });
    message.success("Line quantity cancelled.");
    load();
  }

  if (!isNew && !mrq) return null;

  return (
    <div>
      <PageHeader
        title={isNew ? "New Material Request" : mrq!.docNo}
        subtitle={<SiteLabel site={site} />}
        extra={
          <Space className="glut-no-print">
            {mrq && (
              <span data-tour="doc-status">
                <DocumentStatusTag status={mrq.status} />
              </span>
            )}
            {editable && canWrite && (
              <Button type="primary" onClick={save} loading={loading} data-tour="mrq-save">
                Save
              </Button>
            )}
            {!isNew && mrq?.status === "DRAFT" && canWrite && (
              <Button onClick={submit} loading={loading} data-tour="mrq-submit">
                Submit for approval
              </Button>
            )}
            {!isNew && (mrq?.status === "DRAFT" || mrq?.status === "PENDING_APPROVAL") && canWrite && (
              <Button danger onClick={cancelMRQ} loading={loading} data-tour="mrq-cancel">
                Cancel
              </Button>
            )}
            {/* Each downstream document is offered only when its own allocation bucket has
                something outstanding — an enabled button that leads to an empty screen is
                how the issue/allocate dead-end happened. */}
            {showAllocationPanel && procurementOutstanding > 0.0005 && canDraftPR && (
              <Tooltip title={`${procurementOutstanding} allocated to procurement — draft a purchase requisition for it`}>
                <Button type="primary" onClick={draftPR} loading={loading} data-tour="mrq-raise-pr">
                  Raise requisition
                </Button>
              </Tooltip>
            )}
            {showAllocationPanel && (
              <Tooltip title={cwOutstanding > 0.0005 ? undefined : "Nothing on this request has an undispatched central-warehouse allocation."}>
                <Button disabled={cwOutstanding <= 0.0005} onClick={() => navigate(`/issues/new?mrqId=${id}`)} data-tour="mrq-create-issue">
                  Create issue
                </Button>
              </Tooltip>
            )}
            {showAllocationPanel && (
              <Tooltip title={transferOutstanding > 0.0005 ? undefined : "Nothing on this request has an undispatched site-transfer allocation."}>
                <Button disabled={transferOutstanding <= 0.0005} onClick={() => navigate(`/transfers/new?mrqId=${id}`)} data-tour="mrq-create-transfer">
                  Create transfer
                </Button>
              </Tooltip>
            )}
            {!isNew && <Button onClick={() => window.print()}>Print</Button>}
          </Space>
        }
      />
      <Card size="small" style={{ marginBottom: 16 }} className="glut-no-print">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Site">
            {isNew ? (
              <SiteSelect
                data-tour="mrq-site"
                style={{ width: 240 }}
                placeholder="Select a site"
                options={selectableSites.map((s) => ({ value: s.id, label: s.name }))}
                value={siteId}
                onChange={(v) => {
                  setSiteId(v);
                  setActivityIds([]);
                  setLines([]);
                }}
              />
            ) : (
              <SiteLabel site={site} />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Needed by">
            {editable ? (
              <DatePicker
                data-tour="mrq-needed-by"
                value={neededBy ?? (mrq?.neededBy ? dayjs(mrq.neededBy) : undefined)}
                onChange={(v) => setNeededBy(v ?? undefined)}
              />
            ) : (
              mrq?.neededBy && dayjs(mrq.neededBy).format(DT_FORMATS.date)
            )}
          </Descriptions.Item>
          {/* The work comes first: a request is raised for activities, and only the
              materials approved under them can be asked for (spec §7). */}
          <Descriptions.Item label="Activities" span={2}>
            <ActivityScopeSelect
              data-tour="mrq-activities"
              activities={activityTree}
              value={activityIds}
              onChange={changeActivities}
              readOnly={!editable}
              disabled={activityTree.length === 0}
              placeholder={activityTree.length === 0 ? "Select a site with an approved BOM first" : undefined}
            />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Lines" className="glut-no-print" data-tour="mrq-lines">
        {editable && activityIds.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Select the activities this request is for"
            description="Materials are requested against a piece of work. Choosing an activity offers the materials approved under it — and everything under its sub-activities."
          />
        )}
        <Table<DraftLine>
          rowKey="key"
          dataSource={lines}
          pagination={false}
          size="small"
          columns={[
            {
              // Its own column so the code stays scannable down the page, in both the
              // editable and the read-only rendering of the line.
              title: "Code",
              key: "itemCode",
              width: 120,
              render: (_, row) => (row.bomItemId ? bomItemById.get(row.bomItemId)?.item?.code ?? "-" : "-"),
            },
            {
              title: "BOM Item",
              render: (_, row) => {
                const bl = row.bomItemId ? bomItemById.get(row.bomItemId) : undefined;
                if (!editable) {
                  if (!bl) return "-";
                  return (
                    <Space direction="vertical" size={0}>
                      <span>{bl.item?.name ?? bl.itemId}</span>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {bomItemActivityPath(bl, activities)}
                      </Typography.Text>
                    </Space>
                  );
                }
                const used = new Set(lines.filter((l) => l.key !== row.key).map((l) => l.bomItemId));
                return (
                  <Select
                    showSearch
                    style={{ width: 320 }}
                    placeholder={activityIds.length === 0 ? "Select an activity first" : "Select a BOM item"}
                    disabled={activityIds.length === 0}
                    value={row.bomItemId}
                    // Grouped by activity: a requester picks a material under the piece of
                    // work it was approved for, which is the only way to tell two rows for
                    // the same material apart.
                    options={groupedBomItemOptions.map((g) => ({
                      label: g.label,
                      options: g.options.filter((o) => !used.has(o.value)),
                    }))}
                    filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    onChange={(v) => {
                      // Switching to a tighter item must not leave an over-balance qty behind.
                      const balance = bomItemBalance(bomItemById.get(v)!);
                      updateLine(row.key, {
                        bomItemId: v,
                        requestedQty: row.requestedQty == null ? undefined : Math.min(row.requestedQty, balance),
                      });
                    }}
                  />
                );
              },
            },
            {
              title: "Requested qty",
              width: 260,
              render: (_, row) => {
                const bl = row.bomItemId ? bomItemById.get(row.bomItemId) : undefined;
                if (!editable) return `${row.requestedQty ?? 0} ${bl?.item?.unit?.abbreviation ?? ""}`;
                const balance = bl ? bomItemBalance(bl) : undefined;
                return (
                  <Space.Compact style={{ display: "flex" }}>
                    <QtyInput
                      value={row.requestedQty}
                      unit={bl?.item?.unit?.abbreviation}
                      max={balance}
                      onChange={(v) => updateLine(row.key, { requestedQty: v ?? undefined })}
                    />
                    <Tooltip title={balance === undefined ? "Select a BOM item first." : balance > 0.0005 ? `Request the whole remaining balance (${balance})` : "Nothing left on this BOM item's balance."}>
                      <Button disabled={!balance || balance <= 0.0005} onClick={() => updateLine(row.key, { requestedQty: balance })}>
                        All
                      </Button>
                    </Tooltip>
                  </Space.Compact>
                );
              },
            },
            ...(!isNew && mrq
              ? [
                  {
                    title: "Approved / Allocated / Dispatched",
                    key: "progress",
                    render: (_: unknown, row: DraftLine) => {
                      const l = mrq.lines?.find((x) => String(x.id) === row.key);
                      if (!l) return "-";
                      // A declined line has no quantities to report — what it needs to say
                      // is that it was asked for and refused, by whom, and why.
                      if (l.rejected) {
                        return (
                          <Space direction="vertical" size={0}>
                            <Tag color="red">Not approved</Tag>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {l.rejecter?.name ?? l.rejecter?.email ?? "Declined"}
                              {l.rejectedAt ? ` on ${dayjs(l.rejectedAt).format(DT_FORMATS.date)}` : ""}
                              {l.rejectionReason ? ` - "${l.rejectionReason}"` : ""}
                            </Typography.Text>
                          </Space>
                        );
                      }
                      return `${l.approvedQty} / ${l.allocatedQty} / ${l.dispatchedQty}`;
                    },
                  },
                ]
              : []),
            ...(editable
              ? [
                  {
                    title: "",
                    key: "remove",
                    width: 48,
                    render: (_: unknown, row: DraftLine) => <Button icon={<DeleteOutlined />} danger type="text" onClick={() => removeLine(row.key)} />,
                  },
                ]
              : []),
          ]}
        />
        {editable && (
          <Space style={{ marginTop: 8 }} wrap>
            <Button icon={<PlusOutlined />} onClick={addLine} disabled={activityIds.length === 0} data-tour="mrq-add-line">
              Add line
            </Button>
            <Tooltip title={activityIds.length === 0 ? "Select an activity first." : "One line per material still outstanding on the selected activities, pre-filled with its whole remaining balance."}>
              <Button icon={<PlusOutlined />} onClick={addAllFromScope} disabled={scopedBomItems.length === 0} data-tour="mrq-add-all">
                Add all items from selected activities
              </Button>
            </Tooltip>
          </Space>
        )}
      </Card>

      {showAllocationPanel && mrq && (
        <Card size="small" title="Fulfillment allocation" className="glut-no-print" style={{ marginTop: 16 }} data-tour="mrq-allocation">
          {procurementOutstanding > 0.0005 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${procurementOutstanding} allocated to procurement — this quantity has to be bought before it can be issued`}
              description={
                livePRs.length > 0
                  ? "A requisition is already in progress. It becomes issuable stock for this request once the purchase order is received (GRN), which converts the procurement allocation into a central-warehouse one."
                  : "Procurement allocations are not dispatchable: raise a requisition, and the accepted GRN against its purchase order converts the allocation into a central-warehouse one you can then issue."
              }
              action={
                livePRs.length === 0 && canDraftPR ? (
                  <Button onClick={draftPR} loading={loading}>
                    Raise requisition
                  </Button>
                ) : undefined
              }
            />
          )}
          <RelatedDocRow label="Requisitions" docs={docs.requisitions} to={(d) => `/procurement/prs/${d.id}`} />
          <RelatedDocRow label="Issues" docs={docs.issues} to={(d) => `/issues/${d.id}`} />
          {/* Sits between issues and receipts because it is both: the supplier delivered
              straight to this site, so one document issued and received the material. */}
          <RelatedDocRow label="Direct deliveries" docs={docs.ginDirects} to={(d) => `/procurement/gin-directs/${d.id}`} />
          <RelatedDocRow label="Transfers" docs={docs.transfers} to={(d) => `/transfers/${d.id}`} />
          <RelatedDocRow label="Receipts" docs={docs.receipts} to={(d) => `/receipts/${d.id}`} />
          {(mrq.lines ?? []).filter((l) => !l.rejected).map((l) => (
            <AllocationLinePanel
              key={l.id}
              line={l}
              canAllocate={canAllocate}
              onApplyProposal={() => applyProposal(l.id)}
              onCancelQty={(qty, reason) => cancelLineQty(l.id, qty, reason)}
              onReleaseAllocation={(allocationId, qty, reason) => releaseAllocation(l.id, allocationId, qty, reason)}
            />
          ))}
        </Card>
      )}

      {!isNew && <ApprovalTimeline docType="MRQ" docId={Number(id)} refreshKey={mrq?.status} lines={approvalLines} onActed={load} />}
      {!isNew && mrq && (
        <DocumentPrintSheet
          title="Material Request"
          docNo={mrq.docNo}
          meta={[
            { label: "Site", value: fmtSite(site) },
            { label: "Status", value: mrq.status },
            { label: "Needed by", value: mrq.neededBy ? dayjs(mrq.neededBy).format(DT_FORMATS.date) : "-" },
          ]}
        >
          <Table
            rowKey="id"
            dataSource={mrq.lines ?? []}
            pagination={false}
            columns={[
              { title: "Code", render: (_, r) => r.item?.code },
              { title: "Item", render: (_, r) => r.item?.name },
              { title: "Requested", dataIndex: "requestedQty" },
              { title: "Approved", render: (_, r) => (r.rejected ? "Not approved" : r.approvedQty) },
              { title: "Dispatched", dataIndex: "dispatchedQty" },
            ]}
          />
        </DocumentPrintSheet>
      )}
    </div>
  );
}

function AllocationLinePanel({
  line,
  canAllocate,
  onApplyProposal,
  onCancelQty,
  onReleaseAllocation,
}: {
  line: MRQLine;
  canAllocate: boolean;
  onApplyProposal: () => void;
  onCancelQty: (qty: number, reason: string) => void;
  onReleaseAllocation: (allocationId: number, qty: number, reason: string) => void;
}) {
  const [cancelQty, setCancelQty] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  // Releasing is scoped to one allocation, so the prompt is per row.
  const [releasing, setReleasing] = useState<FulfillmentAllocation | null>(null);
  const [releaseReason, setReleaseReason] = useState("");
  const remaining = line.approvedQty - line.cancelledQty;
  const needsAllocation = line.allocatedQty < remaining - 0.0005;
  const cancellable = line.approvedQty - line.cancelledQty - line.dispatchedQty - line.receivedQty;

  return (
    <Card size="small" type="inner" title={line.item ? fmtItem(line.item) : `Line ${line.id}`} style={{ marginBottom: 12 }}>
      <Descriptions column={4} size="small" style={{ marginBottom: 8 }}>
        <Descriptions.Item label="Approved">{line.approvedQty}</Descriptions.Item>
        <Descriptions.Item label="Allocated">{line.allocatedQty}</Descriptions.Item>
        <Descriptions.Item label="Dispatched">{line.dispatchedQty}</Descriptions.Item>
        <Descriptions.Item label="Cancelled">{line.cancelledQty}</Descriptions.Item>
      </Descriptions>
      {(line.allocations ?? []).length > 0 && (
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={line.allocations}
          columns={[
            { title: "Source", render: (_, a) => <Tag>{a.sourceType.replaceAll("_", " ")}</Tag> },
            { title: "Site", render: (_, a) => <SiteLabel site={a.sourceSite} fallback="-" /> },
            { title: "Qty", dataIndex: "qty" },
            { title: "Dispatched", dataIndex: "dispatchedQty" },
            { title: "Status", render: (_, a) => <DocumentStatusTag status={a.status} /> },
            // Giving up on a source is not the same as giving up on the material: this
            // frees the quantity to be sourced another way, leaving the line's approved
            // quantity intact. "Cancel qty" below is the other decision.
            {
              title: "",
              render: (_, a) =>
                canAllocate && a.status !== "CANCELLED" && a.qty - a.cancelledQty - a.dispatchedQty > 0.0005 ? (
                  <Button
                    size="small"
                    onClick={() => {
                      setReleasing(a);
                      setReleaseReason("");
                    }}
                  >
                    Release
                  </Button>
                ) : null,
            },
          ]}
          style={{ marginBottom: 8 }}
        />
      )}
      <Modal
        open={!!releasing}
        title="Release this allocation"
        okText="Release"
        okButtonProps={{ disabled: !releaseReason }}
        onCancel={() => setReleasing(null)}
        onOk={() => {
          const a = releasing!;
          onReleaseAllocation(a.id, a.qty - a.cancelledQty - a.dispatchedQty, releaseReason);
          setReleasing(null);
        }}
        destroyOnHidden
      >
        {releasing && (
          <>
            <p>
              This gives up on <strong>{releasing.sourceType.replaceAll("_", " ").toLowerCase()}</strong> as the source for{" "}
              <strong>{releasing.qty - releasing.cancelledQty - releasing.dispatchedQty}</strong> unit(s) — use it when a
              purchase will not deliver, or stock has to come from somewhere else.
            </p>
            <p>
              The request still needs the material: that quantity returns to unallocated, ready to be allocated again and
              requisitioned. Anything already dispatched is untouched. To give up on the material itself, cancel the
              quantity on the line instead.
            </p>
            <Input.TextArea
              rows={2}
              placeholder="Reason — required"
              value={releaseReason}
              onChange={(e) => setReleaseReason(e.target.value)}
            />
          </>
        )}
      </Modal>
      {canAllocate && (
        <Space wrap>
          {needsAllocation && (
            <Button size="small" onClick={onApplyProposal} data-tour="mrq-apply-proposal">
              Get &amp; apply allocation proposal
            </Button>
          )}
          {cancellable > 0.0005 && (
            <Space size="small">
              <InputNumber size="small" placeholder="Qty to cancel" min={0} max={cancellable} value={cancelQty ?? undefined} onChange={setCancelQty} />
              <Input size="small" placeholder="Reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} style={{ width: 160 }} />
              <Button
                size="small"
                danger
                disabled={!cancelQty || !cancelReason}
                onClick={() => {
                  onCancelQty(cancelQty!, cancelReason);
                  setCancelQty(null);
                  setCancelReason("");
                }}
              >
                Cancel qty
              </Button>
            </Space>
          )}
        </Space>
      )}
    </Card>
  );
}
